import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../config";
import { authHeaders,getSessionToken } from "../cms/utils/session";

const PLACEHOLDER_OPTIONS = [
  { label: "Lead Name", value: "{{LEAD_NAME}}" },
  { label: "Trademark Name", value: "{{TRADEMARK_NAME}}" },
  { label: "Company Name", value: "{{COMPANY_NAME}}" },
  { label: "Case ID", value: "{{CASE_ID}}" },
  { label: "Registration Number", value: "{{REGISTRATION_NUMBER}}" },
  { label: "Final Status", value: "{{FINAL_STATUS}}" },
  { label: "Attorney Name", value: "{{ATTORNEY_NAME}}" },
  { label: "Total Paid", value: "{{TOTAL_PAID}}" },
  { label: "Dashboard Link", value: "{{DASHBOARD_LINK}}" },
  { label: "Sender Name", value: "{{SENDER_NAME}}" },
  { label: "Company Phone", value: "{{COMPANY_PHONE}}" },
];

const CATEGORIES = [
  { value: "marketing", label: "Marketing", icon: "fa-bullhorn", color: "#f59e0b", bg: "#fffbeb" },
  { value: "utility", label: "Utility", icon: "fa-tools", color: "#556ee6", bg: "#eef2ff" },
  { value: "authentication", label: "Auth", icon: "fa-shield-alt", color: "#22c55e", bg: "#f0fff4" },
];

const CreateWhatsAppTemplate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [placeholderSearch, setPlaceholderSearch] = useState("");

  const [formData, setFormData] = useState({
    template_name: "",
    category: "utility",
    language: "en",
    header_type: "none",
    header_text: "",
    body: "",
    footer: "",
    button_type: "none",
    button_text: "",
    button_url: "",
    status: "active",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const token = getSessionToken();
      const res = await axios.get(`${API_BASE_URL}/get-whatsapp-template/${id}`, {
        headers: { ...authHeaders() },
      });
      if (res.data?.success && res.data.data) {
        const t = res.data.data;
        setFormData({
          template_name: t.template_name || "",
          category: t.category || "utility",
          language: t.language || "en",
          header_type: t.header_type || "none",
          header_text: t.header_text || "",
          body: t.body || "",
          footer: t.footer || "",
          button_type: t.button_type || "none",
          button_text: t.button_text || "",
          button_url: t.button_url || "",
          status: t.status || "active",
        });
      }
    } catch (err) {
      toast.error("Failed to load template");
      navigate("/cms/templates");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const insertPlaceholder = (placeholder) => {
    const el = document.getElementById("wa_body");
    if (el) {
      const start = el.selectionStart || formData.body.length;
      const end = el.selectionEnd || formData.body.length;
      const text = formData.body;
      const newText = text.substring(0, start) + placeholder + text.substring(end);
      setFormData((prev) => ({ ...prev, body: newText }));
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    } else {
      setFormData((prev) => ({ ...prev, body: prev.body + placeholder }));
    }
    toast.info(`Inserted ${placeholder}`, { autoClose: 800 });
  };

  const validate = () => {
    const errs = {};
    if (!formData.template_name.trim()) errs.template_name = "Template name is required";
    if (!formData.body.trim()) errs.body = "Message body is required";
    if (formData.body.length > 1024) errs.body = "Body must be under 1024 characters";
    if (formData.header_type === "text" && !formData.header_text.trim()) errs.header_text = "Header text is required";
    if (formData.button_type !== "none" && !formData.button_text.trim()) errs.button_text = "Button text is required";
    if (formData.button_type === "url" && !formData.button_url.trim()) errs.button_url = "Button URL is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { toast.error("Please fix the errors"); return; }
    try {
      setSaving(true);
      const token = getSessionToken();
      const payload = { ...formData };
      if (payload.header_type === "none") payload.header_text = "";
      if (payload.button_type === "none") { payload.button_text = ""; payload.button_url = ""; }
      const url = id ? `${API_BASE_URL}/update-whatsapp-template/${id}` : `${API_BASE_URL}/create-whatsapp-template`;
      const method = id ? "put" : "post";
      const res = await axios[method](url, payload, { headers: { ...authHeaders() } });
      if (res.data?.success) {
        toast.success(id ? "Template updated" : "Template created");
        navigate("/cms/templates");
      } else { toast.error(res.data?.message || "Something went wrong"); }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save template");
    } finally { setSaving(false); }
  };

  const filteredPlaceholders = PLACEHOLDER_OPTIONS.filter((p) =>
    p.label.toLowerCase().includes(placeholderSearch.toLowerCase())
  );

  const bodyCharCount = formData.body.length;
  const charColor = bodyCharCount > 1024 ? "#ef4444" : bodyCharCount > 900 ? "#f59e0b" : "#94a3b8";

  const inputStyle = { borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", transition: "border-color 0.2s, box-shadow 0.2s" };
  const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "6px" };

  if (loading) {
    return (
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid text-center py-5">
            <div className="spinner-border text-success" role="status"></div>
            <p className="text-muted mt-2">Loading template...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18" style={{ fontWeight: 700, color: "#1f2937" }}>
                  <i className="fab fa-whatsapp me-2" style={{ color: "#25D366" }}></i>
                  {id ? "Edit WhatsApp Template" : "Create WhatsApp Template"}
                </h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item"><Link to="/cms/home">Dashboard</Link></li>
                    <li className="breadcrumb-item"><Link to="/cms/templates">Templates</Link></li>
                    <li className="breadcrumb-item active">{id ? "Edit" : "Create"} WhatsApp</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* LEFT - Form */}
            <div className="col-lg-7">
              <div className="card" style={{ borderRadius: "12px", border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                <div style={{ height: "4px", background: "linear-gradient(90deg, #25D366, #128C7E)" }}></div>
                <div className="card-body" style={{ padding: "28px" }}>
                  <h4 className="card-title mb-4" style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
                    WhatsApp Template Details
                  </h4>

                  {/* Template Name */}
                  <div className="mb-4">
                    <label className="form-label" style={labelStyle}>Template Name <span className="text-danger">*</span></label>
                    <input type="text" name="template_name"
                      className={`form-control ${errors.template_name ? "is-invalid" : ""}`}
                      placeholder="e.g., case_closed_notification"
                      value={formData.template_name} onChange={handleChange} style={inputStyle} />
                    {errors.template_name && <div className="invalid-feedback">{errors.template_name}</div>}
                    <small className="form-text text-muted" style={{ fontSize: "12px" }}>Lowercase, underscores only (WhatsApp naming convention)</small>
                  </div>

                  {/* Category & Language */}
                  <div className="row mb-4">
                    <div className="col-md-7 mb-3 mb-md-0">
                      <label className="form-label" style={labelStyle}>Category</label>
                      <div className="d-flex gap-2">
                        {CATEGORIES.map((cat) => (
                          <button key={cat.value} type="button"
                            className="btn btn-sm flex-fill d-flex align-items-center justify-content-center gap-1"
                            onClick={() => setFormData((prev) => ({ ...prev, category: cat.value }))}
                            style={{
                              borderRadius: "8px", fontSize: "12.5px", fontWeight: 600, padding: "8px 12px",
                              border: `2px solid ${formData.category === cat.value ? cat.color : "#e5e7eb"}`,
                              backgroundColor: formData.category === cat.value ? cat.bg : "#fff",
                              color: formData.category === cat.value ? cat.color : "#6b7280",
                              transition: "all 0.2s",
                            }}
                          >
                            <i className={`fas ${cat.icon}`} style={{ fontSize: "11px" }}></i>{cat.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label" style={labelStyle}>Language</label>
                      <select name="language" className="form-select" value={formData.language} onChange={handleChange} style={inputStyle}>
                        <option value="en">English</option>
                        <option value="hi">Hindi</option>
                        <option value="en_US">English (US)</option>
                      </select>
                    </div>
                  </div>

                  {/* Header */}
                  <div className="mb-4">
                    <label className="form-label" style={labelStyle}>Header</label>
                    <div className="d-flex gap-2 mb-2">
                      {["none", "text", "image"].map((type) => (
                        <button key={type} type="button"
                          className={`btn btn-sm ${formData.header_type === type ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setFormData((prev) => ({ ...prev, header_type: type }))}
                          style={{ textTransform: "capitalize", borderRadius: "7px", fontSize: "12px", fontWeight: 600, padding: "5px 14px" }}
                        >{type}</button>
                      ))}
                    </div>
                    {formData.header_type === "text" && (
                      <>
                        <input type="text" name="header_text" className={`form-control ${errors.header_text ? "is-invalid" : ""}`}
                          placeholder="Header text..." value={formData.header_text} onChange={handleChange} style={inputStyle} />
                        {errors.header_text && <div className="invalid-feedback">{errors.header_text}</div>}
                      </>
                    )}
                  </div>

                  {/* Body */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label mb-0" style={labelStyle}>Message Body <span className="text-danger">*</span></label>
                      <span style={{ fontSize: "12px", fontWeight: 700, color: charColor }}>{bodyCharCount}/1024</span>
                    </div>
                    <textarea id="wa_body" name="body"
                      className={`form-control ${errors.body ? "is-invalid" : ""}`}
                      rows="6" placeholder="Type your message here... Use placeholders for dynamic content."
                      value={formData.body} onChange={handleChange} style={{ ...inputStyle, resize: "vertical" }} />
                    {errors.body && <div className="invalid-feedback">{errors.body}</div>}
                  </div>

                  {/* Footer */}
                  <div className="mb-4">
                    <label className="form-label" style={labelStyle}>Footer <small className="text-muted fw-normal">(optional)</small></label>
                    <input type="text" name="footer" className="form-control" placeholder="e.g., Powered by YourCompany"
                      value={formData.footer} onChange={handleChange} maxLength={60} style={inputStyle} />
                  </div>

                  {/* Button */}
                  <div className="mb-4">
                    <label className="form-label" style={labelStyle}>Button</label>
                    <div className="d-flex gap-2 mb-2">
                      {[
                        { value: "none", label: "None" },
                        { value: "url", label: "Visit Website" },
                        { value: "quick_reply", label: "Quick Reply" },
                      ].map((bt) => (
                        <button key={bt.value} type="button"
                          className={`btn btn-sm ${formData.button_type === bt.value ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setFormData((prev) => ({ ...prev, button_type: bt.value }))}
                          style={{ borderRadius: "7px", fontSize: "12px", fontWeight: 600, padding: "5px 14px" }}
                        >{bt.label}</button>
                      ))}
                    </div>
                    {formData.button_type !== "none" && (
                      <div className="row g-2">
                        <div className={formData.button_type === "url" ? "col-md-5" : "col-12"}>
                          <input type="text" name="button_text" className={`form-control ${errors.button_text ? "is-invalid" : ""}`}
                            placeholder="Button label" value={formData.button_text} onChange={handleChange} style={inputStyle} />
                          {errors.button_text && <div className="invalid-feedback">{errors.button_text}</div>}
                        </div>
                        {formData.button_type === "url" && (
                          <div className="col-md-7">
                            <input type="url" name="button_url" className={`form-control ${errors.button_url ? "is-invalid" : ""}`}
                              placeholder="https://example.com/..." value={formData.button_url} onChange={handleChange} style={inputStyle} />
                            {errors.button_url && <div className="invalid-feedback">{errors.button_url}</div>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="mb-4">
                    <label className="form-label" style={labelStyle}>Status</label>
                    <div className="d-flex gap-3">
                      {[
                        { value: "active", label: "Active", color: "#22c55e", bg: "#f0fff4" },
                        { value: "inactive", label: "Inactive", color: "#ef4444", bg: "#fff5f5" },
                      ].map((s) => (
                        <label key={s.value} className="d-flex align-items-center gap-2"
                          style={{
                            padding: "8px 18px", borderRadius: "8px",
                            border: `2px solid ${formData.status === s.value ? s.color : "#e5e7eb"}`,
                            backgroundColor: formData.status === s.value ? s.bg : "#fff",
                            cursor: "pointer", transition: "all 0.2s", fontSize: "13px", fontWeight: 600,
                            color: formData.status === s.value ? s.color : "#6b7280",
                          }}
                        >
                          <input type="radio" name="status" value={s.value} checked={formData.status === s.value} onChange={handleChange} style={{ display: "none" }} />
                          <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: formData.status === s.value ? s.color : "#d1d5db" }}></span>
                          {s.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex justify-content-between align-items-center pt-3" style={{ borderTop: "1px solid #f1f5f9" }}>
                    <button className="btn btn-light d-flex align-items-center gap-2" onClick={() => navigate("/cms/templates")}
                      style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600, color: "#64748b" }}>
                      <i className="fas fa-arrow-left" style={{ fontSize: "11px" }}></i> Back
                    </button>
                    <button className="btn d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving}
                      style={{ background: "linear-gradient(135deg, #25D366, #128C7E)", color: "#fff", borderRadius: "8px", fontSize: "13px", fontWeight: 600, padding: "8px 24px", border: "none", boxShadow: "0 3px 10px rgba(37,211,102,0.3)" }}>
                      {saving ? (
                        <><span className="spinner-border spinner-border-sm"></span> Saving...</>
                      ) : (
                        <><i className="fas fa-save" style={{ fontSize: "12px" }}></i> {id ? "Update" : "Save"} Template</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT - Preview + Placeholders */}
            <div className="col-lg-5">

              {/* WhatsApp Live Preview */}
              <div className="card" style={{ borderRadius: "12px", border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                <div className="card-body p-0">
                  <div className="d-flex align-items-center gap-2 px-3 py-2" style={{ backgroundColor: "#075E54" }}>
                    <div className="d-flex align-items-center justify-content-center" style={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.15)" }}>
                      <i className="fab fa-whatsapp text-white" style={{ fontSize: "18px" }}></i>
                    </div>
                    <div>
                      <p className="mb-0 text-white" style={{ fontSize: "14px", fontWeight: 600 }}>Live Preview</p>
                      <p className="mb-0" style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>WhatsApp Business</p>
                    </div>
                  </div>
                  <div className="p-3" style={{ backgroundColor: "#ECE5DD", minHeight: "200px", backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"6\" height=\"6\" viewBox=\"0 0 6 6\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"%23c8c3ba\" fill-opacity=\"0.12\"%3E%3Cpath d=\"M5 0h1L0 6V5zm1 5v1H5z\"/%3E%3C/g%3E%3C/svg%3E')" }}>
                    <div style={{ maxWidth: "88%", backgroundColor: "#fff", borderRadius: "0 10px 10px 10px", padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", wordBreak: "break-word" }}>
                      {formData.header_type === "text" && formData.header_text && (
                        <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: "14px", color: "#1a1a1a" }}>{formData.header_text}</p>
                      )}
                      {formData.header_type === "image" && (
                        <div className="d-flex align-items-center justify-content-center mb-2" style={{ height: "90px", borderRadius: "8px", backgroundColor: "#f0f0f0" }}>
                          <i className="fas fa-image" style={{ fontSize: "22px", color: "#c4c4c4" }}></i>
                        </div>
                      )}
                      <p style={{ margin: 0, fontSize: "13.5px", color: "#303030", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                        {formData.body || <span style={{ color: "#999" }}>Your message will appear here...</span>}
                      </p>
                      {formData.footer && (
                        <p style={{ margin: "8px 0 0", fontSize: "11px", color: "#8e8e8e" }}>{formData.footer}</p>
                      )}
                      <div className="text-end" style={{ marginTop: "3px" }}>
                        <small style={{ fontSize: "10px", color: "#8e8e8e" }}>
                          {new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                          <i className="fas fa-check-double ms-1" style={{ fontSize: "10px", color: "#53bdeb" }}></i>
                        </small>
                      </div>
                    </div>
                    {formData.button_type !== "none" && formData.button_text && (
                      <div style={{ maxWidth: "88%", marginTop: "3px" }}>
                        <div className="text-center bg-white" style={{ padding: "10px", borderRadius: "10px", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}>
                          <span style={{ fontSize: "13px", fontWeight: 600, color: "#00a884" }}>
                            {formData.button_type === "url" && <i className="fas fa-external-link-alt me-1" style={{ fontSize: "11px" }}></i>}
                            {formData.button_text}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Placeholders */}
              <div className="card" style={{ borderRadius: "12px", border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                <div style={{ height: "4px", background: "linear-gradient(90deg, #25D366, #128C7E)" }}></div>
                <div className="card-body" style={{ padding: "24px" }}>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", borderRadius: "8px", backgroundColor: "#f0fff4" }}>
                      <i className="fas fa-code" style={{ color: "#25D366", fontSize: "14px" }}></i>
                    </div>
                    <h5 className="mb-0" style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>Placeholders</h5>
                  </div>
                  <div className="position-relative mb-3">
                    <i className="fas fa-search position-absolute" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", color: "#a0aec0", fontSize: "12px" }}></i>
                    <input type="text" className="form-control form-control-sm" placeholder="Search placeholders..."
                      value={placeholderSearch} onChange={(e) => setPlaceholderSearch(e.target.value)}
                      style={{ paddingLeft: "30px", borderRadius: "7px", border: "1px solid #e2e8f0", fontSize: "12.5px" }} />
                  </div>
                  <div className="d-flex flex-wrap gap-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {filteredPlaceholders.map((p, i) => (
                      <button key={i} type="button" className="btn btn-sm" onClick={() => insertPlaceholder(p.value)}
                        style={{ fontSize: "11.5px", fontWeight: 500, padding: "4px 11px", borderRadius: "6px", backgroundColor: "#f0fff4", color: "#15803d", border: "1px solid #bbf7d0", transition: "all 0.2s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#25D366"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#25D366"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#f0fff4"; e.currentTarget.style.color = "#15803d"; e.currentTarget.style.borderColor = "#bbf7d0"; }}
                      >{p.label}</button>
                    ))}
                    {filteredPlaceholders.length === 0 && (
                      <p className="text-muted text-center w-100 mb-0" style={{ fontSize: "13px" }}>No matching placeholders</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWhatsAppTemplate;